/**
 * Every `/api/v1` call the AI writing assistant makes.
 *
 * Each path is checked against `backend/src/modules/ai/routes.ts`, which is the only place these
 * endpoints exist.
 *
 * ```
 * GET    /brands/:brandId/ai/style
 * PUT    /brands/:brandId/ai/style/:provider
 * DELETE /brands/:brandId/ai/style/:provider
 * GET    /brands/:brandId/ai/credits
 * GET    /brands/:brandId/ai/credits/history
 * POST   /brands/:brandId/ai/credits/checkout
 * POST   /brands/:brandId/ai/assist
 * POST   /brands/:brandId/ai/scheduling-jobs/preview
 * POST   /brands/:brandId/ai/scheduling-jobs
 * GET    /brands/:brandId/ai/scheduling-jobs
 * GET    /brands/:brandId/ai/scheduling-jobs/membership
 * GET    /brands/:brandId/ai/scheduling-jobs/:jobId
 * GET    /brands/:brandId/ai/scheduling-jobs/:jobId/posts
 * PATCH  /brands/:brandId/ai/scheduling-jobs/:jobId
 * POST   /brands/:brandId/ai/scheduling-jobs/:jobId/confirm
 * POST   /brands/:brandId/ai/scheduling-jobs/:jobId/save-as-drafts
 * POST   /brands/:brandId/ai/scheduling-jobs/:jobId/cancel
 * GET    /brands/:brandId/ai/chat
 * POST   /brands/:brandId/ai/chat
 * GET    /brands/:brandId/ai/chat/:sessionId
 * POST   /brands/:brandId/ai/chat/:sessionId/messages
 * DELETE /brands/:brandId/ai/chat/:sessionId
 * ```
 *
 * There is no "reset" body: resetting is `DELETE`, because absence of a stored row is what
 * "follows the default" means on the server. Writing the default text back would freeze the
 * brand at today's wording.
 */

import type { AiCreditBalance, AiCreditHistory, Provider } from '@anubis-connector/shared'

import { apiV1, apiV1Void } from '../../lib/api'
import type {
  AiStyle,
  AiStyleSettings,
  AssistInput,
  AssistResult,
  ChatSession,
  ChatSessionSummary,
  ConfirmedPostInput,
  DraftedPostInput,
  GroupPost,
  SchedulingJob,
  SchedulingJobGroup,
  SchedulingJobInput,
  SchedulingJobMembership,
  SchedulingJobPreview,
} from './types'

const base = (brandId: string): string => `/brands/${encodeURIComponent(brandId)}/ai`

const stylePath = (brandId: string, provider: Provider): string =>
  `${base(brandId)}/style/${encodeURIComponent(provider)}`

export const fetchAiSettings = (brandId: string): Promise<AiStyleSettings> =>
  apiV1<AiStyleSettings>(`${base(brandId)}/style`)

export const saveAiStyle = (
  brandId: string,
  provider: Provider,
  instructions: string,
): Promise<AiStyle> =>
  apiV1<AiStyle>(stylePath(brandId, provider), { method: 'PUT', json: { instructions } })

export const resetAiStyle = (brandId: string, provider: Provider): Promise<AiStyle> =>
  apiV1<AiStyle>(stylePath(brandId, provider), { method: 'DELETE' })

export const fetchAiCreditBalance = (brandId: string): Promise<AiCreditBalance> =>
  apiV1<AiCreditBalance>(`${base(brandId)}/credits`)

/** Every purchase this brand ever made, and a year of usage month by month — the dedicated
    credits page's one call. */
export const fetchAiCreditHistory = (brandId: string): Promise<AiCreditHistory> =>
  apiV1<AiCreditHistory>(`${base(brandId)}/credits/history`)

/**
 * Start a purchase of `credits` writing credits. Returns the Stripe url to send the browser to.
 *
 * The balance is credited by Stripe's **webhook**, not by the redirect — a tab closed after
 * paying must not lose what was paid for — so the screen that comes back refetches the balance
 * rather than assuming it.
 */
export const buyAiCredits = (brandId: string, credits: number): Promise<{ url: string }> =>
  apiV1<{ url: string }>(`${base(brandId)}/credits/checkout`, {
    method: 'POST',
    json: { credits },
  })

/**
 * Ask for one caption.
 *
 * No retry and no fallback text: if this rejects, the composer shows the server's own sentence.
 * A canned caption presented as the model's would be indistinguishable from a real one and would
 * quietly go out as a published post.
 */
export const requestAssist = (brandId: string, input: AssistInput): Promise<AssistResult> =>
  apiV1<AssistResult>(`${base(brandId)}/assist`, { method: 'POST', json: input })

/* ── the batch scheduler ───────────────────────────────────────────────────────────────────
   ```
   POST  /brands/:brandId/ai/scheduling-jobs
   GET   /brands/:brandId/ai/scheduling-jobs/:jobId
   PATCH /brands/:brandId/ai/scheduling-jobs/:jobId
   POST  /brands/:brandId/ai/scheduling-jobs/:jobId/confirm
   POST  /brands/:brandId/ai/scheduling-jobs/:jobId/save-as-drafts
   POST  /brands/:brandId/ai/scheduling-jobs/:jobId/cancel
   ```
   Confirm is the only one that *schedules* anything. Save-as-drafts still creates real posts —
   unarmed ones — which is the point of it: the review screen's generated text used to live only
   in this tab until confirm, and closing early meant it (and the credits already spent on it)
   were simply gone. */

const jobPath = (brandId: string, jobId?: string): string =>
  jobId === undefined
    ? `${base(brandId)}/scheduling-jobs`
    : `${base(brandId)}/scheduling-jobs/${encodeURIComponent(jobId)}`

/**
 * Draft a batch. Costs one credit per caption the assistant writes, and schedules nothing.
 *
 * The drafting runs inside this request — the server models it as a job so the proposal, the
 * review and the confirm are all about the same durable thing, not so the client has to wait on
 * a queue. `fetchSchedulingJob` is there for the cases where a client did lose the answer.
 */
export const createSchedulingJob = (
  brandId: string,
  input: SchedulingJobInput,
): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(jobPath(brandId), { method: 'POST', json: input })

/** What drafting this brief would do — free, no model call, nothing written. */
export const previewSchedulingJob = (
  brandId: string,
  input: SchedulingJobInput,
): Promise<SchedulingJobPreview> =>
  apiV1<SchedulingJobPreview>(`${jobPath(brandId)}/preview`, { method: 'POST', json: input })

export const fetchSchedulingJob = (brandId: string, jobId: string): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(jobPath(brandId, jobId))

/**
 * Schedule the reviewed batch. **This is the call that creates posts.**
 *
 * `posts` is the batch as the author left it — edited captions, moved times, entries removed by
 * leaving them out. Omitting it schedules the proposal unchanged.
 */
export const confirmSchedulingJob = (
  brandId: string,
  jobId: string,
  posts?: readonly ConfirmedPostInput[],
): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(`${jobPath(brandId, jobId)}/confirm`, {
    method: 'POST',
    json: posts === undefined ? {} : { posts },
  })

export const cancelSchedulingJob = (brandId: string, jobId: string): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(`${jobPath(brandId, jobId)}/cancel`, { method: 'POST' })

/**
 * Keep the batch without arming it. **This also creates posts** — drafts, not scheduled ones —
 * so it is the call the review dialog makes on any close (the X, Escape, the backdrop, the
 * button): nothing generated is ever silently lost again, whatever the author presses.
 */
export const saveAsDraftsSchedulingJob = (
  brandId: string,
  jobId: string,
  posts?: readonly DraftedPostInput[],
): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(`${jobPath(brandId, jobId)}/save-as-drafts`, {
    method: 'POST',
    json: posts === undefined ? {} : { posts },
  })

/** Rename the group. Empty clears it back to the computed fallback. */
export const renameSchedulingJob = (
  brandId: string,
  jobId: string,
  name: string,
): Promise<SchedulingJob> =>
  apiV1<SchedulingJob>(jobPath(brandId, jobId), { method: 'PATCH', json: { name } })

/* ── groups ("Gruppi") ──────────────────────────────────────────────────────────────────────
   ```
   GET /brands/:brandId/ai/scheduling-jobs
   GET /brands/:brandId/ai/scheduling-jobs/membership
   GET /brands/:brandId/ai/scheduling-jobs/:jobId/posts
   ```
   All three are reads — a group's own detail opens its posts through the ordinary `?post=<id>`
   deep link, not through these. */

export const fetchSchedulingJobGroups = (brandId: string): Promise<SchedulingJobGroup[]> =>
  apiV1<SchedulingJobGroup[]>(jobPath(brandId))

export const fetchSchedulingJobPosts = (brandId: string, jobId: string): Promise<GroupPost[]> =>
  apiV1<GroupPost[]>(`${jobPath(brandId, jobId)}/posts`)

/** Which of these calendar post ids belong to a group — `PostCard`'s badge. Empty in, empty
 *  out, so a view with nothing loaded yet never fires the request. */
export const fetchSchedulingJobMembership = (
  brandId: string,
  postIds: readonly number[],
): Promise<SchedulingJobMembership> =>
  postIds.length === 0
    ? Promise.resolve({})
    : apiV1<SchedulingJobMembership>(
        `${base(brandId)}/scheduling-jobs/membership?postIds=${postIds.join(',')}`,
      )

/* ── the conversational assistant ──────────────────────────────────────────────────────────
   ```
   GET    /brands/:brandId/ai/chat
   POST   /brands/:brandId/ai/chat
   GET    /brands/:brandId/ai/chat/:sessionId
   POST   /brands/:brandId/ai/chat/:sessionId/messages
   DELETE /brands/:brandId/ai/chat/:sessionId
   ```
   Five calls, and none of them changes anything outside a transcript: the assistant discusses the
   brand's own numbers and drafts copy, and scheduling, publishing and settings stay in the flows
   that own them. There is no endpoint here that could do otherwise. */

const chatPath = (brandId: string, sessionId?: string): string =>
  sessionId === undefined
    ? `${base(brandId)}/chat`
    : `${base(brandId)}/chat/${encodeURIComponent(sessionId)}`

export const fetchChatSessions = (brandId: string): Promise<ChatSessionSummary[]> =>
  apiV1<ChatSessionSummary[]>(chatPath(brandId))

export const fetchChatSession = (brandId: string, sessionId: string): Promise<ChatSession> =>
  apiV1<ChatSession>(chatPath(brandId, sessionId))

/**
 * Open a conversation with its first message. Costs one credit, for the one reply.
 *
 * No retry and no fallback text, as with every other call in this file: a failure shows the
 * server's own sentence, and nothing is written — a conversation that got no answer does not
 * appear in the list.
 */
export const startChat = (brandId: string, message: string): Promise<ChatSession> =>
  apiV1<ChatSession>(chatPath(brandId), { method: 'POST', json: { message } })

export const sendChatMessage = (
  brandId: string,
  sessionId: string,
  message: string,
): Promise<ChatSession> =>
  apiV1<ChatSession>(`${chatPath(brandId, sessionId)}/messages`, {
    method: 'POST',
    json: { message },
  })

export const deleteChatSession = (brandId: string, sessionId: string): Promise<void> =>
  apiV1Void(chatPath(brandId, sessionId), { method: 'DELETE' })
